export type UploadResult = {
  key: string;
  url: string;
};

const DEFAULT_PUBLIC_DOMAIN = "https://pub-2520cfe8f11f4f9ab4d2e59663aef88e.r2.dev";

export const CLOUDFLARE_UPLOAD_ENDPOINT =
  process.env.REACT_APP_CLOUDFLARE_UPLOAD_ENDPOINT || "";

export const CLOUDFLARE_AUTH_KEY =
  process.env.REACT_APP_CLOUDFLARE_AUTH_KEY || "";

export const CLOUDFLARE_PUBLIC_DOMAIN =
  (process.env.REACT_APP_CLOUDFLARE_PUBLIC_DOMAIN || DEFAULT_PUBLIC_DOMAIN).replace(/\/+$/, "");

/**
 * Upload an image file directly to Cloudflare R2 via the Cloudflare Worker upload endpoint.
 */
export async function uploadImageToR2(
  file: File,
  projectId: string = "general",
  endpointUrl: string = CLOUDFLARE_UPLOAD_ENDPOINT,
  authKey: string = CLOUDFLARE_AUTH_KEY
): Promise<UploadResult> {
  const targetEndpoint = endpointUrl.trim();

  if (!targetEndpoint) {
    throw new Error(
      "Cloudflare Upload endpoint is not configured. Set REACT_APP_CLOUDFLARE_UPLOAD_ENDPOINT or enter your Worker URL."
    );
  }

  // Validate file type
  if (!file.type.startsWith("image/")) {
    throw new Error(`File "${file.name}" is not a valid image format.`);
  }

  // Limit max file size to 25MB per image
  const MAX_SIZE = 25 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    throw new Error(`Image "${file.name}" exceeds the 25MB size limit.`);
  }

  const formData = new FormData();
  formData.append("file", file);

  const headers: Record<string, string> = {
    "X-Project-Id": projectId.trim().toLowerCase() || "general",
    "X-File-Name": file.name,
  };

  if (authKey.trim()) {
    headers["X-Custom-Auth-Key"] = authKey.trim();
  }

  const response = await fetch(targetEndpoint, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorDetail = `Upload failed with status ${response.status}`;
    try {
      const errorJson = await response.json();
      if (errorJson.error || errorJson.details) {
        errorDetail = errorJson.details || errorJson.error;
      }
    } catch {
      // Fallback
    }
    throw new Error(errorDetail);
  }

  const result = await response.json();
  if (!result.url) {
    throw new Error("Worker responded without an image URL.");
  }

  return {
    key: result.key,
    url: result.url,
  };
}
